/**
 * Supporter Context
 *
 * Manages supporter subscription state throughout the app.
 * Handles subscription status, checkout flow, and feature gating.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import {
  fetchSubscriptionStatus,
  createCheckoutSession,
  createPortalSession,
  SupporterFeatures,
} from '../api/client';

interface SupporterState {
  tier: 'free' | 'supporter' | 'trialing';
  status: 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'expired';
  trialEndsAt: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  features: SupporterFeatures;
  isLoading: boolean;
  error: string | null;
}

interface SupporterContextType extends SupporterState {
  /** Whether the user has an active supporter subscription (active or trialing) */
  isSupporter: boolean;
  /** Refresh subscription status from the server */
  refreshStatus: () => Promise<void>;
  /** Start the Stripe checkout flow */
  startCheckout: () => Promise<void>;
  /** Open the Stripe customer portal for subscription management */
  openPortal: () => Promise<void>;
  /** Check if a specific feature is available */
  hasFeature: (feature: keyof SupporterFeatures) => boolean;
}

const defaultFeatures: SupporterFeatures = {
  highResLayers: false,
  fireSpreadAnalysis: false,
  satelliteImagery: false,
};

const defaultState: SupporterState = {
  tier: 'free',
  status: 'none',
  trialEndsAt: null,
  currentPeriodEnd: null,
  cancelAtPeriodEnd: false,
  features: defaultFeatures,
  isLoading: true,
  error: null,
};

const SupporterContext = createContext<SupporterContextType | null>(null);

export function SupporterProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, getAccessToken } = useAuth();
  const [state, setState] = useState<SupporterState>(defaultState);

  // Load subscription status when authentication changes
  useEffect(() => {
    if (isAuthenticated) {
      loadSubscriptionStatus();
    } else {
      // Reset to default state when logged out
      setState(defaultState);
    }
  }, [isAuthenticated]);

  const loadSubscriptionStatus = async () => {
    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const token = await getAccessToken();
      if (!token) {
        setState(prev => ({ ...prev, isLoading: false }));
        return;
      }

      const status = await fetchSubscriptionStatus(token);

      setState({
        tier: status.tier,
        status: status.status,
        trialEndsAt: status.trialEndsAt,
        currentPeriodEnd: status.currentPeriodEnd,
        cancelAtPeriodEnd: status.cancelAtPeriodEnd,
        features: status.features,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      console.error('[SupporterContext] Error loading subscription status:', err);
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: err instanceof Error ? err.message : 'Failed to load subscription status',
      }));
    }
  };

  const refreshStatus = useCallback(async () => {
    await loadSubscriptionStatus();
  }, [isAuthenticated]);

  const startCheckout = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Please log in to become a supporter');
      }

      // Create checkout session
      const currentUrl = window.location.origin;
      const session = await createCheckoutSession(
        {
          successUrl: `${currentUrl}?subscription=success`,
          cancelUrl: `${currentUrl}?subscription=canceled`,
        },
        token
      );

      // Redirect to Stripe Checkout
      window.location.href = session.url;
    } catch (err) {
      console.error('[SupporterContext] Error starting checkout:', err);
      throw err;
    }
  }, [getAccessToken]);

  const openPortal = useCallback(async () => {
    try {
      const token = await getAccessToken();
      if (!token) {
        throw new Error('Please log in to manage your subscription');
      }

      // Create portal session
      const session = await createPortalSession(
        { returnUrl: window.location.href },
        token
      );

      // Redirect to Stripe Customer Portal
      window.location.href = session.url;
    } catch (err) {
      console.error('[SupporterContext] Error opening portal:', err);
      throw err;
    }
  }, [getAccessToken]);

  const hasFeature = useCallback((feature: keyof SupporterFeatures): boolean => {
    return state.features[feature] ?? false;
  }, [state.features]);

  // Check for successful subscription on page load (after Stripe redirect)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const subscriptionParam = params.get('subscription');

    if (subscriptionParam === 'success' && isAuthenticated) {
      // Refresh subscription status after successful checkout
      setTimeout(() => {
        loadSubscriptionStatus();
      }, 1000); // Small delay to allow webhook to process

      // Clean up URL
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('subscription');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [isAuthenticated]);

  const isSupporter = state.tier === 'supporter' && ['active', 'trialing'].includes(state.status);

  const value: SupporterContextType = {
    ...state,
    isSupporter,
    refreshStatus,
    startCheckout,
    openPortal,
    hasFeature,
  };

  return (
    <SupporterContext.Provider value={value}>
      {children}
    </SupporterContext.Provider>
  );
}

export function useSupporter() {
  const context = useContext(SupporterContext);
  if (!context) {
    throw new Error('useSupporter must be used within a SupporterProvider');
  }
  return context;
}

/**
 * Hook to check if a specific supporter feature is available.
 * Returns false if the user is not a supporter.
 */
export function useSupporterFeature(feature: keyof SupporterFeatures): boolean {
  const { hasFeature } = useSupporter();
  return hasFeature(feature);
}
